import { DashboardReminder } from "Shared/Types/dashboard";
import {
  REMINDERS_CONTACTS_UPDATE_ID,
  REMINDERS_SERVICES_ID,
  REMINDERS_VEHICLES_EDIT,
} from "Shared/Constants/routes";

export type AttentionReminder = DashboardReminder["results"][number];

const priority: Record<string, number> = {
  overdue: 0,
  due_soon: 1,
  upcoming: 2,
};

// Service reminders use PascalCase while renewals use snake_case.
// Normalize presentation only; do not replace the API record's status.
export const normalizeReminderStatus = (status: string) => {
  const normalized = (status || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
  return normalized === "duesoon" ? "due_soon" : normalized;
};

// Server status is authoritative: dates alone cannot express mileage-based urgency.
export const prioritizeReminders = (rows: AttentionReminder[]) =>
  [...rows].sort(
    (a, b) =>
      (priority[normalizeReminderStatus(a.status)] ?? 3) -
      (priority[normalizeReminderStatus(b.status)] ?? 3),
  );

export const reminderStatusLabel = (status: string) => {
  const normalized = normalizeReminderStatus(status);
  if (!normalized) return "Status unavailable";
  return normalized
    .replace(/_/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
};

export const reminderDetailPath = (item: AttentionReminder) => {
  const id = encodeURIComponent(item.id);
  if (item.type === "service_reminder") return REMINDERS_SERVICES_ID(id);
  return item.type === "vehicle_renewal"
    ? `${REMINDERS_VEHICLES_EDIT}/${id}`
    : REMINDERS_CONTACTS_UPDATE_ID(id);
};

export const reminderDueLabel = (date: string | null) => {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return "Date unavailable";
  const [year, month, day] = date.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  )
    return "Date unavailable";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
};
